import React from 'react';
import { Box, Typography, IconButton, Stack, Grid } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { GameListSummary } from '../../services/contents_api';
import { useNavigate } from 'react-router-dom';

interface GameListComponentProps {
  list: GameListSummary;
  onEdit?: (list: GameListSummary) => void;
  onDelete?: (listId: number) => void;
}

const MAX_COVERS = 4; // 最大表示カバー数
// カバーをカード幅の何%にするか。4枚目が右端でぴったり揃う設計:
//   1枚目: 0%、2枚目: OFFSET%、3枚目: OFFSET*2%、4枚目: OFFSET*3%
//   4枚目の右端 = OFFSET*3 + WIDTH = 100% になるよう OFFSET = (100-WIDTH)/3
const COVER_WIDTH_PCT = 42;
const COVER_OFFSET_PCT = (100 - COVER_WIDTH_PCT) / (MAX_COVERS - 1);
// コンテナの aspect-ratio: width / height
//   height = (CARD_WIDTH * COVER_WIDTH_PCT/100) * 4/3 (カバーが 3:4 縦長)
//   → ratio = 100 / (COVER_WIDTH_PCT * 4 / 3) = 75 / (COVER_WIDTH_PCT)
const COVER_STACK_ASPECT = 75 / COVER_WIDTH_PCT; // 42% → ~1.79

const GameListComponent: React.FC<GameListComponentProps> = ({ list, onEdit, onDelete }) => {
  const isPrivate = list.visibility === 0;
  const navigate = useNavigate();

  const handleTitleClick = () => {
    navigate(`/list/${list.id}`);
  };

  const handleEdit = () => {
    navigate(`/list/${list.id}/edit`);
  };

  return (
    <Grid item xs={6} sm={4} md={3} lg={3}>
      <Box
        onClick={handleTitleClick}
        sx={{
          cursor: 'pointer',
          p: { xs: 0.4, sm: 0.6, md: 0.75 },
          borderRadius: 1.25,
          transition: 'background-color 0.18s ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
          },
        }}
      >
        {/* スタックされたカバー画像 */}
        <Box
          className="cover-stack"
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: `${COVER_STACK_ASPECT}`,
            mb: { xs: 0.6, sm: 0.85, md: 1.1 },
          }}
        >
          {list.preview_items.slice(0, MAX_COVERS).map((
            item: { id: number; game_cover: string | null },
            index: number,
            array: Array<{ id: number; game_cover: string | null }>
          ) => (
            <Box
              key={item.id}
              sx={{
                position: 'absolute',
                top: 0,
                left: `${index * COVER_OFFSET_PCT}%`,
                width: `${COVER_WIDTH_PCT}%`,
                aspectRatio: '3 / 4',
                borderRadius: 1,
                overflow: 'hidden',
                transition:
                  'transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.28s ease',
                zIndex: array.length - index,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.25)',
                '&:hover': {
                  transform: 'translateY(-10px) scale(1.04)',
                  boxShadow: '0 12px 22px rgba(0, 0, 0, 0.4)',
                  zIndex: array.length + 1,
                },
              }}
            >
              <Box
                component="img"
                src={item.game_cover || '/image/no_image.png'}
                alt=""
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                }}
              />
            </Box>
          ))}
        </Box>

        {/* リスト情報 */}
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: { xs: 0.25, sm: 0.5 },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                component="h3"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                  lineHeight: 1.2,
                  mb: 0.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {list.title}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0.4, sm: 0.7 },
                  flexWrap: 'wrap',
                  rowGap: 0.3,
                }}
              >
                {isPrivate && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.3,
                      backgroundColor: 'rgba(255, 255, 255, 0.06)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: '3px',
                      padding: { xs: '1px 4px', sm: '2px 6px' },
                      color: 'rgba(255, 255, 255, 0.55)',
                    }}
                  >
                    <LockOutlinedIcon
                      sx={{ fontSize: { xs: '0.55rem', sm: '0.65rem' } }}
                    />
                    <Typography
                      component="span"
                      sx={{
                        fontSize: { xs: '0.55rem', sm: '0.62rem' },
                        fontWeight: 400,
                        lineHeight: 1,
                      }}
                    >
                      非公開
                    </Typography>
                  </Box>
                )}
                {list.list_type === 2 && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.3,
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                      border: '1px solid rgba(255, 215, 0, 0.5)',
                      borderRadius: '3px',
                      padding: { xs: '1px 4px', sm: '2px 6px' },
                      color: '#FFD700',
                    }}
                  >
                    <WorkspacePremiumIcon
                      sx={{ fontSize: { xs: '0.58rem', sm: '0.68rem' } }}
                    />
                    <Typography
                      component="span"
                      sx={{
                        fontSize: { xs: '0.55rem', sm: '0.65rem' },
                        fontWeight: 400,
                        lineHeight: 1,
                      }}
                    >
                      ranking
                    </Typography>
                  </Box>
                )}
                {list.list_type === 4 && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.3,
                      backgroundColor: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '3px',
                      padding: { xs: '1px 4px', sm: '2px 6px' },
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 10, sm: 12 },
                        height: { xs: 6, sm: 6 },
                        borderRadius: 0,
                        background:
                          'linear-gradient(90deg, #ef6171 0%, #e7bd5a 50%, #5e9fcf 100%)',
                      }}
                    />
                    <Typography
                      component="span"
                      sx={{
                        fontSize: { xs: '0.55rem', sm: '0.62rem' },
                        fontWeight: 500,
                        marginLeft: '0.1rem',
                        letterSpacing: '0.06em',
                        lineHeight: 1,
                        color: 'rgba(255,255,255,0.72)',
                        fontFamily:
                          '"Inter", "SF Pro Display", "Roboto", sans-serif',
                      }}
                    >
                      tier
                    </Typography>
                  </Box>
                )}
                <Typography
                  component="span"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.6rem', sm: '0.68rem' },
                    lineHeight: 1,
                  }}
                >
                  {list.item_count}本
                </Typography>
              </Box>
            </Box>
            <Stack
              direction="row"
              spacing={{ xs: 0, sm: 0.25 }}
              sx={{ flexShrink: 0, mt: { xs: -0.25, sm: 0 } }}
            >
              {onEdit && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit();
                  }}
                  size="small"
                  sx={{
                    opacity: 0.7,
                    padding: { xs: '2px', sm: '4px' },
                    '&:hover': { opacity: 1 },
                  }}
                >
                  <EditIcon sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }} />
                </IconButton>
              )}
              {onDelete && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(list.id);
                  }}
                  size="small"
                  color="error"
                  sx={{
                    opacity: 0.7,
                    padding: { xs: '2px', sm: '4px' },
                    '&:hover': { opacity: 1 },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }} />
                </IconButton>
              )}
            </Stack>
          </Box>
        </Box>
      </Box>
    </Grid>
  );
};

export default GameListComponent; 